import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiUsers, FiCheck, FiLoader, FiSend } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCreateRoundWizard } from '../../contexts/CreateRoundWizardContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getFriends } from '../../services/friendService';
import { getUserGroups } from '../../services/groupService';
import AvatarBadge from '../../components/AvatarBadge';
import {
  calculateOptimalTimeSlots,
  createManualRound,
  createSoloRound,
} from '../../services/roundService';
import { logPlanUsage } from '../../services/planService';
import { getGroupAvailabilitySummary } from '../../services/availabilityService';
import {
  requestGroupAvailability,
  requestFriendAvailability,
} from '../../services/availabilityRequestService';
import { useRewardedGate } from '../../contexts/RewardedGateContext';
import { hapticSuccess } from '../../services/hapticsService';

const WHO_CARDS = [
  { id: 'solo',    label: 'Solo',    sub: 'Schedule just for yourself',           icon: FiUser },
  { id: 'friends', label: 'Friends', sub: 'Play with one or more of your friends', icon: FiUsers },
  { id: 'group',   label: 'Group',   sub: 'Play with one of your golf groups',     icon: FiUsers },
];

const SLOTS_MIN = 2;
const SLOTS_MAX = 4;

// Copy for the "allow friends to request to join" (open-invite) toggle, by
// scheduling mode. Friends keeps the original wording; solo and group get
// mode-appropriate phrasing since their rosters differ.
const REQUEST_COPY = {
  solo: {
    title: 'Allow friends to request to join',
    description: "We'll let your friends know you're playing so they can request to join.",
  },
  friends: {
    title: 'Allow other friends to request to join',
    description: 'Friends not invited directly will be notified and can request a spot.',
  },
  group: {
    title: 'Allow friends to request to join',
    description: 'Friends not in this group will be notified and can request a spot.',
  },
};

function SelectableCard({ selected, disabled, onClick, name, title, subtitle, badge, icon: Icon, fullWidth = false }) {
  return (
    <motion.button
      whileHover={disabled ? undefined : { scale: 1.02 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`flex items-center p-3 rounded-xl border transition-all duration-200 min-h-[60px] text-left ${
        selected
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-border-subtle bg-card hover:border-border-subtle text-text-secondary'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''} ${fullWidth ? 'w-full' : ''}`}
    >
      {Icon ? (
        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
          selected ? 'bg-accent text-white' : 'bg-surface text-text-secondary border border-border-subtle'
        }`}>
          <SafeIcon icon={Icon} className="w-5 h-5" />
        </div>
      ) : (
        <AvatarBadge name={name || title} size="md" selected={selected} className="mr-3" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{title}</p>
        {subtitle && <p className="text-xs opacity-70 truncate">{subtitle}</p>}
        {badge && (
          <span className="px-2 py-0.5 mt-1 inline-block bg-accent/10 text-accent text-[10px] font-semibold rounded-full">
            {badge}
          </span>
        )}
      </div>
      {selected && <SafeIcon icon={FiCheck} className="w-4 h-4 text-accent flex-shrink-0 ml-2" />}
    </motion.button>
  );
}

// Friend / group-member row with optional per-member availability indicator.
// Behaviour mirrors the legacy SmartScheduleSetup / SmartScheduleFriends rows:
// green = "Available on N days", amber = "No availability in selected dates",
// red = "No availability set" with a Request CTA. The availability slot
// is suppressed entirely when `availability` is null (e.g. Quick mode, or
// the summary hasn't loaded yet on the current context).
function MemberRow({
  selected,
  disabled,
  onClick,
  name,
  title,
  badge,
  availability,
  availabilityLoading,
  requestState,
  onRequest,
}) {
  const renderAvailability = () => {
    if (!availability && !availabilityLoading) return null;
    const text = availabilityLoading && !availability ? 'Checking…' : availability?.text;
    const colour = !availability
      ? 'text-text-muted'
      : availability.status === 'available'
        ? 'text-accent'
        : availability.status === 'limited'
          ? 'text-amber-500'
          : 'text-status-error';
    const showRequest = availability?.status === 'none';
    const buttonLabel =
      requestState === 'sending' ? 'Sending…'
      : requestState === 'sent' ? 'Sent'
      : requestState === 'rate_limited' ? 'Recently sent'
      : requestState === 'failed' ? 'Retry'
      : 'Request';
    return (
      <div className="flex items-center gap-2 mt-0.5">
        <span className={`text-xs truncate ${colour}`}>{text}</span>
        {showRequest && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRequest?.(); }}
            disabled={requestState === 'sending' || requestState === 'sent'}
            className="text-[10px] font-medium text-accent hover:underline whitespace-nowrap disabled:opacity-50 disabled:no-underline"
          >
            {buttonLabel}
          </button>
        )}
      </div>
    );
  };

  return (
    <motion.button
      whileHover={disabled ? undefined : { scale: 1.02 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`flex items-center p-3 rounded-xl border transition-all duration-200 min-h-[60px] text-left w-full ${
        selected
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-border-subtle bg-card hover:border-border-subtle text-text-secondary'
      } ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
    >
      <AvatarBadge name={name || title} size="md" selected={selected} className="mr-3" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{title || name}</p>
        {renderAvailability()}
        {badge && (
          <span className="px-2 py-0.5 mt-1 inline-block bg-accent/10 text-accent text-[10px] font-semibold rounded-full">
            {badge}
          </span>
        )}
      </div>
      {selected && <SafeIcon icon={FiCheck} className="w-4 h-4 text-accent flex-shrink-0 ml-2" />}
    </motion.button>
  );
}

function StepWho() {
  const navigate = useNavigate();
  const { state, setWho, setWhoPickerOpen, setFriendSelection, toggleAnyFriends, setSlots, setGroup, setGroupMembers } = useCreateRoundWizard();
  const { user } = useAuth();
  const toast = useToast();
  const { gate } = useRewardedGate();

  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Availability summary keyed by member user_id. Populated for the current
  // picker context (friends or group) whenever the wizard is in smart mode
  // and we have a date range to check against. Mirrors the legacy
  // SmartScheduleSetup / SmartScheduleFriends behaviour so smart-schedule
  // honours member availability instead of blindly assuming everyone is free.
  const [availabilityCheck, setAvailabilityCheck] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityRequested, setAvailabilityRequested] = useState({});

  // Load friends and groups eagerly so the cards can be disabled when the
  // user has none of either. Cheap RPCs; runs once on mount.
  useEffect(() => {
    let cancelled = false;
    setFriendsLoading(true);
    setGroupsLoading(true);
    Promise.all([getFriends(), getUserGroups()]).then(([friendsRes, groupsRes]) => {
      if (cancelled) return;
      setFriends(friendsRes?.data || []);
      const groupRows = Array.isArray(groupsRes) ? groupsRes : (groupsRes?.data || []);
      setGroups(groupRows);
      setFriendsLoading(false);
      setGroupsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const selectedGroup = useMemo(
    () => groups.find(g => g.id === state.selectedGroupId) || null,
    [groups, state.selectedGroupId],
  );

  const toggleFriend = (friendId) => {
    const set = new Set(state.selectedFriendIds);
    if (set.has(friendId)) set.delete(friendId);
    else set.add(friendId);
    setFriendSelection(Array.from(set));
  };

  // Roster math for the open-to-requests toggle and player-cap buttons, by
  // mode. currentCount is who's already on the roster (creator + whoever's
  // directly on it): solo = just you; friends = you + invited friends; group =
  // the selected group members (which already include you). The toggle only
  // makes sense when the user has friends to broadcast to AND there's an open
  // seat (currentCount < 4).
  const hasFriends = friends.length > 0;
  const currentCount = state.who === 'group'
    ? Math.max(1, state.selectedGroupMemberIds.length)
    : state.who === 'friends'
    ? 1 + state.selectedFriendIds.length
    : 1; // solo
  const rosterFull = currentCount >= SLOTS_MAX;
  // Friends-only: there's no one left to broadcast to once every friend is
  // already a direct invite.
  const allFriendsInvited = state.who === 'friends'
    && hasFriends
    && state.selectedFriendIds.length >= friends.length;
  const requestsDisabled = rosterFull || allFriendsInvited;
  const allowRequestsActive = hasFriends && state.anyFriends && !requestsDisabled;
  const minCap = Math.max(SLOTS_MIN, currentCount);
  const slotOptions = [];
  for (let n = minCap; n <= SLOTS_MAX; n += 1) slotOptions.push(n);

  // Keep state.slots valid: if the roster grows past the previously-chosen
  // cap, bump the cap up to match. Runs whenever the toggle is active.
  useEffect(() => {
    if (!allowRequestsActive) return;
    if (state.slots < minCap) setSlots(Math.min(SLOTS_MAX, minCap));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minCap, allowRequestsActive]);

  const toggleGroupMember = (memberId) => {
    if (memberId === user?.id) return;
    const set = new Set(state.selectedGroupMemberIds);
    if (set.has(memberId)) set.delete(memberId);
    else set.add(memberId);
    setGroupMembers(Array.from(set));
  };

  // ---------------------------------------------------------------------------
  // Availability summary (smart mode only)
  //
  // Fetch a per-member availability summary as soon as the user lands on the
  // friends or group picker AND we have a smart-mode date range. Re-fetches
  // when the roster changes (new group selected, friends loaded) or when the
  // date range moves. Quick mode bails out — there's no range to compare.
  // ---------------------------------------------------------------------------

  const isSmart = state.mode === 'smart';
  const dateRange = state.dateRange;

  // Build the membership set the summary should cover. For friends-context
  // we summarise the whole friend list so the badge renders on every row,
  // not just selected ones. For group-context we summarise every member of
  // the currently-selected group.
  const summaryContext = useMemo(() => {
    if (!isSmart) return null;
    if (!dateRange?.startDate || !dateRange?.endDate) return null;

    if (state.who === 'friends') {
      const ids = [user?.id, ...friends.map(f => f.friend_id)].filter(Boolean);
      if (ids.length < 2) return null;
      return { kind: 'friends', groupId: null, memberIds: ids };
    }

    if (state.who === 'group') {
      const group = groups.find(g => g.id === state.selectedGroupId);
      if (!group) return null;
      const ids = [
        user?.id,
        ...((group.members || []).map(m => m.userId).filter(Boolean)),
      ].filter((v, i, arr) => v && arr.indexOf(v) === i);
      if (ids.length < 2) return null;
      return { kind: 'group', groupId: group.id, memberIds: ids };
    }

    return null;
  }, [isSmart, dateRange?.startDate, dateRange?.endDate, state.who, state.selectedGroupId, friends, groups, user?.id]);

  useEffect(() => {
    if (!summaryContext) {
      setAvailabilityCheck(null);
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    getGroupAvailabilitySummary(
      summaryContext.groupId,
      summaryContext.memberIds,
      dateRange.startDate,
      dateRange.endDate,
    )
      .then(summary => { if (!cancelled) setAvailabilityCheck(summary); })
      .catch(err => {
        if (!cancelled) {
          console.error('[StepWho] availability summary failed:', err);
          setAvailabilityCheck(null);
        }
      })
      .finally(() => { if (!cancelled) setAvailabilityLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryContext]);

  // Reset request status when the context changes (different group / mode)
  useEffect(() => {
    setAvailabilityRequested({});
  }, [state.who, state.selectedGroupId]);

  // Returns { status: 'available' | 'limited' | 'none', text } or null when
  // we're not in smart mode / haven't fetched yet.
  const getAvailability = (memberId) => {
    if (!availabilityCheck?.details?.[memberId]) return null;
    const detail = availabilityCheck.details[memberId];
    if (detail.hasAvailability && detail.availabilityInRange > 0) {
      const qualifyingDays = state.holeCount === 9
        ? (detail.daysWithMin2Hours || 0)
        : (detail.daysWithMin4Hours || 0);
      const dayWord = qualifyingDays === 1 ? 'day' : 'days';
      return {
        status: 'available',
        text: qualifyingDays > 0
          ? `Available on ${qualifyingDays} ${dayWord}`
          : 'Limited availability',
      };
    }
    if (detail.hasAnyAvailabilityInNext6Months && detail.availabilityInNext6Months > 0) {
      return { status: 'limited', text: 'No availability in selected dates' };
    }
    return { status: 'none', text: 'No availability set' };
  };

  const handleRequestAvailability = async (memberId) => {
    if (availabilityRequested[memberId]) return;
    setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'sending' }));
    try {
      // Match the period info to whatever the wizard's WHEN step settled on.
      const period = {
        dateRange: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      };

      if (state.who === 'group' && state.selectedGroupId) {
        const group = groups.find(g => g.id === state.selectedGroupId);
        const requesterName = user?.user_metadata?.full_name
          || user?.user_metadata?.display_name
          || user?.email?.split('@')[0]
          || 'A group member';
        await requestGroupAvailability(
          state.selectedGroupId,
          user.id,
          requesterName,
          group?.name || 'your group',
          [memberId],
          period,
        );
        setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'sent' }));
      } else if (state.who === 'friends') {
        const { results } = await requestFriendAvailability([memberId], period);
        const outcome = results?.[0]?.outcome;
        if (outcome === 'sent') {
          setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'sent' }));
        } else if (outcome === 'rate_limited') {
          setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'rate_limited' }));
        } else {
          setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'failed' }));
        }
      }
    } catch (err) {
      console.error('[StepWho] request availability failed:', err);
      setAvailabilityRequested(prev => ({ ...prev, [memberId]: 'failed' }));
    }
  };

  const canSubmit = useMemo(() => {
    if (!state.who) return false;
    if (state.who === 'solo') return true;
    if (state.who === 'friends') {
      return state.selectedFriendIds.length > 0 || allowRequestsActive;
    }
    if (state.who === 'group') {
      return !!state.selectedGroupId && state.selectedGroupMemberIds.length >= 2;
    }
    return false;
  }, [state, allowRequestsActive]);

  // Open-invite config applies in any mode once the toggle is active. The
  // audience is always the creator's friends (audienceAllFriends); friends
  // already on the round just can't re-join, so a broadcast is harmless.
  const buildOpenInviteConfig = () => {
    if (!allowRequestsActive) return null;
    return {
      isOpenInvite: true,
      audienceAllFriends: true,
      friendIds: [],
      joinMode: 'review',
      maxPlayers: state.slots,
    };
  };

  const handleSmartContinue = async (whoOverride) => {
    if (submitting) return;
    const who = whoOverride || state.who;
    setSubmitting(true);
    try {
      // iOS rewarded ad-gate: 1 free Smart Schedule run/month, then watch an
      // ad or go Pro. No-op on web / for Pro / when an ad is unavailable.
      if (!(await gate('smart_schedule'))) { setSubmitting(false); return; }

      const openInvite = buildOpenInviteConfig();

      let selectedMembers = [user?.id].filter(Boolean);
      if (who === 'friends') {
        selectedMembers = Array.from(new Set([user?.id, ...state.selectedFriendIds].filter(Boolean)));
      } else if (who === 'group') {
        selectedMembers = Array.from(new Set([user?.id, ...state.selectedGroupMemberIds].filter(Boolean)));
      }

      const courseName = state.course?.name || state.course;
      const result = await calculateOptimalTimeSlots(
        who === 'group' ? state.selectedGroupId : null,
        state.dateRange,
        selectedMembers,
        state.holeCount,
        courseName,
        user?.id,
      );
      const analysis = result?.data || result;
      const resultData = analysis?.data || analysis || [];
      const validatedResults = Array.isArray(resultData) ? resultData : [];

      logPlanUsage(
        'smart_schedule',
        { mode: who === 'group' ? 'group' : who, groupId: state.selectedGroupId || null },
        user,
      );

      const memberNames = {};
      if (who === 'friends') {
        friends.forEach(f => { memberNames[f.friend_id] = f.full_name; });
      } else if (who === 'group' && selectedGroup) {
        (selectedGroup.members || []).forEach(m => {
          if (m.userId) memberNames[m.userId] = m.name || m.member_name || 'Member';
        });
      }
      if (user) {
        memberNames[user.id] = user.user_metadata?.full_name || user.user_metadata?.display_name || 'You';
      }

      const baseState = {
        results: validatedResults,
        selectedMembers,
        memberNames,
        dateRange: state.dateRange,
        holeCount: state.holeCount,
        selectedCourse: state.course,
        availabilityData: analysis?.availabilityData || null,
        openInvite,
      };

      if (who === 'solo') {
        navigate('/smart-schedule-results/solo', { state: { ...baseState, roundType: 'solo' } });
      } else if (who === 'friends') {
        navigate('/smart-schedule-results/friends', {
          state: { ...baseState, selectedGroup: null, roundType: 'friends' },
        });
      } else {
        navigate(`/smart-schedule-results/${state.selectedGroupId}`, {
          state: { ...baseState, selectedGroup, roundType: 'group' },
        });
      }
    } catch (err) {
      console.error('Smart wizard handoff error:', err);
      toast.error(err.message || 'Failed to analyse times. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickCreate = async (whoOverride) => {
    if (submitting) return;
    const who = whoOverride || state.who;
    setSubmitting(true);
    try {
      // iOS rewarded ad-gate: 1 free concurrent upcoming round, then watch an
      // ad or go Pro. No-op on web / for Pro / when an ad is unavailable.
      if (!(await gate('planned_round'))) { setSubmitting(false); return; }

      const datetime = `${state.date}T${state.time}:00`;
      const openInvite = buildOpenInviteConfig();
      const courseName = state.course?.name || state.course;

      let roundResult;
      if (who === 'solo') {
        roundResult = await createSoloRound({
          datetime,
          golfClub: courseName,
          holeCount: state.holeCount,
          openInvite,
        }, user);
        if (roundResult?.error) throw roundResult.error;
        hapticSuccess();
        toast.success('Solo round created.');
        navigate('/rounds');
      } else if (who === 'friends') {
        const memberIds = Array.from(new Set([user?.id, ...state.selectedFriendIds].filter(Boolean)));
        roundResult = await createManualRound({
          groupId: null,
          datetime,
          selectedMembers: memberIds,
          golfCourse: courseName,
          holeCount: state.holeCount,
          openInvite,
        });
        if (roundResult?.error) throw roundResult.error;
        // Open-invite fan-out is applied inside createManualRound; do not call
        // applyOpenInviteSettings again here or every audience member is
        // notified twice.
        hapticSuccess();
        toast.success(allowRequestsActive ? 'Invitations sent. Friends can request to join.' : 'Invitations sent.');
        navigate('/rounds');
      } else if (who === 'group') {
        if (state.selectedGroupMemberIds.length < 2) {
          toast.warning('Group rounds require at least 2 participants');
          setSubmitting(false);
          return;
        }
        roundResult = await createManualRound({
          groupId: state.selectedGroupId,
          groupName: selectedGroup?.name || 'Golf Group',
          datetime,
          selectedMembers: state.selectedGroupMemberIds,
          golfCourse: courseName,
          holeCount: state.holeCount,
          openInvite,
        });
        if (roundResult?.error) throw roundResult.error;
        hapticSuccess();
        toast.success('Round created.');
        navigate(`/group/${state.selectedGroupId}`);
      }
    } catch (err) {
      console.error('Quick wizard create error:', err);
      toast.error(err.message || 'Failed to create round. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (whoOverride) => {
    if (state.mode === 'smart') return handleSmartContinue(whoOverride);
    return handleQuickCreate(whoOverride);
  };

  const handleCardClick = (cardId) => {
    setWho(cardId);
    // Per-mode default for the open-to-requests toggle: ON for friends (the
    // original behaviour), OFF for solo and group.
    toggleAnyFriends(cardId === 'friends');
    // Solo with no friends has nothing to configure, so create immediately
    // (passing cardId explicitly since `state.who` hasn't flushed yet). Solo
    // *with* friends opens the options screen so the toggle can be offered.
    if (cardId === 'solo' && !friendsLoading && friends.length === 0) {
      handleSubmit('solo');
    } else {
      setWhoPickerOpen(true);
    }
  };

  const submitLabel = useMemo(() => {
    if (submitting) return state.mode === 'smart' ? 'Finding times…' : 'Creating…';
    if (state.mode === 'smart') return 'Continue';
    if (state.who === 'friends' && allowRequestsActive) return 'Broadcast to friends';
    if (state.who === 'friends') return 'Send invites & create';
    if (state.who === 'solo') return 'Create solo round';
    if (state.who === 'group') return 'Create round';
    return 'Continue';
  }, [submitting, state.mode, state.who, allowRequestsActive]);

  // Reusable open-to-requests toggle + player-cap block, rendered in the solo,
  // friends and group pickers. Hidden entirely when the user has no friends to
  // broadcast to.
  const renderRequestBlock = () => {
    if (!hasFriends) return null;
    const copy = REQUEST_COPY[state.who] || REQUEST_COPY.friends;
    const description = rosterFull
      ? "You've already filled the round, so there's no room for extra requests."
      : (state.who === 'friends' && allFriendsInvited)
      ? 'Everyone you can invite is already selected.'
      : copy.description;
    return (
      <>
        <div className="mt-6 flex items-start justify-between gap-4">
          <div>
            <h4 className="text-sm font-semibold text-text-primary">{copy.title}</h4>
            <p className="text-xs text-text-secondary mt-0.5">{description}</p>
          </div>
          <button
            type="button"
            onClick={() => !requestsDisabled && toggleAnyFriends(!state.anyFriends)}
            disabled={requestsDisabled}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 transition-colors duration-200 ${
              allowRequestsActive
                ? 'bg-accent border-accent'
                : 'bg-surface border-border-subtle'
            } ${requestsDisabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
            aria-pressed={allowRequestsActive}
            aria-label={copy.title}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow border border-border-subtle transition duration-200 ${
                allowRequestsActive ? 'translate-x-[18px]' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {allowRequestsActive && (
          <div className="mt-5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
              Max no. of players (incl. you)
            </label>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${slotOptions.length}, minmax(0, 1fr))` }}>
              {slotOptions.map(n => {
                const active = state.slots === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setSlots(n)}
                    className={`py-3 rounded-xl border-2 text-sm font-semibold transition-colors min-h-[44px] ${
                      active
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border-subtle bg-card text-text-secondary hover:border-accent/40'
                    }`}
                  >
                    {n} Players
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  const showCards = !state.whoPickerOpen;
  const showSoloPicker = state.whoPickerOpen && state.who === 'solo';
  const showFriendsPicker = state.whoPickerOpen && state.who === 'friends';
  const showGroupPicker = state.whoPickerOpen && state.who === 'group';

  return (
    <div>
      {showCards && (
        <>
          <p className="hidden sm:block text-sm text-text-muted mb-6">
            Pick how you want to play this round.
          </p>

          <div className="space-y-3">
            {WHO_CARDS.map(card => {
              const noFriends   = card.id === 'friends' && !friendsLoading && friends.length === 0;
              const noGroups    = card.id === 'group'   && !groupsLoading  && groups.length === 0;
              const empty       = noFriends || noGroups;
              const soloPending = submitting && card.id === 'solo';
              const disabled    = empty || soloPending;
              const subtitle    = noFriends
                ? "You haven't added any friends yet"
                : noGroups
                ? "You're not in any groups yet"
                : card.sub;
              return (
                <motion.button
                  key={card.id}
                  whileHover={disabled ? undefined : { scale: 1.01 }}
                  whileTap={disabled ? undefined : { scale: 0.99 }}
                  onClick={disabled ? undefined : () => handleCardClick(card.id)}
                  disabled={disabled}
                  className="w-full flex items-center text-left gap-3 p-4 rounded-xl border-2 border-border-subtle bg-card hover:border-accent/40 transition-colors min-h-[60px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border-subtle"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface text-text-secondary">
                    <SafeIcon icon={card.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-text-primary">{card.label}</h3>
                    <p className={`${empty ? 'block' : 'hidden sm:block'} text-xs text-text-muted`}>{subtitle}</p>
                  </div>
                  {soloPending && (
                    <SafeIcon icon={FiLoader} className="w-4 h-4 text-accent animate-spin" />
                  )}
                </motion.button>
              );
            })}
          </div>
        </>
      )}

      {showSoloPicker && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">
            Solo round
          </label>
          <p className="text-sm text-text-muted">
            Just you on the course.
            {hasFriends ? ' Optionally let your friends know in case they want to join you.' : ''}
          </p>

          {/* Open-to-requests toggle + player-cap (solo copy). Hidden when the
              user has no friends to broadcast to. */}
          {renderRequestBlock()}
        </div>
      )}

      {showFriendsPicker && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">
            Select friends
          </label>

          {friendsLoading && (
            <div className="flex items-center text-sm text-text-muted">
              <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
              Loading friends…
            </div>
          )}

          {!friendsLoading && friends.length === 0 && (
            <p className="text-sm text-text-muted">You haven't added any friends yet.</p>
          )}

          {!friendsLoading && friends.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {friends.map(friend => {
                const isSelected = state.selectedFriendIds.includes(friend.friend_id);
                const avail = isSmart ? getAvailability(friend.friend_id) : null;
                return (
                  <MemberRow
                    key={friend.friend_id}
                    selected={isSelected}
                    onClick={() => toggleFriend(friend.friend_id)}
                    name={friend.full_name || 'Friend'}
                    availability={avail}
                    availabilityLoading={availabilityLoading}
                    requestState={availabilityRequested[friend.friend_id]}
                    onRequest={() => handleRequestAvailability(friend.friend_id)}
                  />
                );
              })}
            </div>
          )}

          {/* Open-to-requests toggle + player-cap (mode-aware copy). */}
          {renderRequestBlock()}
        </div>
      )}

      {showGroupPicker && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">
            Select a group
          </label>
            {groupsLoading && (
              <div className="flex items-center text-sm text-text-muted">
                <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
                Loading groups…
              </div>
            )}
            {!groupsLoading && groups.length === 0 && (
              <p className="text-sm text-text-muted">You're not in any groups yet.</p>
            )}
            {!groupsLoading && groups.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {groups.map(group => {
                  const groupSelected = state.selectedGroupId === group.id;
                  return (
                    <SelectableCard
                      key={group.id}
                      selected={groupSelected}
                      onClick={() => {
                        const memberIds = (group.members || [])
                          .map(m => m.userId)
                          .filter(Boolean);
                        setGroup(group.id, memberIds);
                      }}
                      icon={FiUsers}
                      title={group.name}
                      subtitle={`${group.members?.length || 0} members`}
                    />
                  );
                })}
              </div>
            )}

            {selectedGroup && (
              <div className="mt-5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">
                  Select members
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(selectedGroup.members || [])
                    .filter(m => m.userId)
                    .sort((a, b) => (a.userId === user?.id ? -1 : b.userId === user?.id ? 1 : 0))
                    .map(member => {
                      const isCurrentUser = member.userId === user?.id;
                      const isSelected = state.selectedGroupMemberIds.includes(member.userId);
                      const displayName = isCurrentUser ? 'You' : (member.name || member.member_name || 'Member');
                      const avatarName = isCurrentUser ? (user?.user_metadata?.full_name || 'You') : displayName;
                      const avail = isSmart && !isCurrentUser ? getAvailability(member.userId) : null;
                      return (
                        <MemberRow
                          key={member.userId}
                          selected={isSelected || isCurrentUser}
                          disabled={isCurrentUser}
                          onClick={() => toggleGroupMember(member.userId)}
                          name={avatarName}
                          title={displayName}
                          badge={isCurrentUser ? 'Required' : null}
                          availability={avail}
                          availabilityLoading={availabilityLoading}
                          requestState={availabilityRequested[member.userId]}
                          onRequest={() => handleRequestAvailability(member.userId)}
                        />
                      );
                    })}
                </div>
              </div>
            )}

            {/* Open-to-requests toggle + player-cap (group copy). Only once a
                group is picked, so it reads as "below member selection". */}
            {selectedGroup && renderRequestBlock()}
        </div>
      )}

      {(showSoloPicker || showFriendsPicker || showGroupPicker) && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => handleSubmit()}
            disabled={!canSubmit || submitting}
            className="px-6 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 font-semibold disabled:opacity-50 flex items-center min-h-[44px]"
          >
            {submitting ? (
              <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
            )}
            {submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default StepWho;
